import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { IDynamicContainerMetadata } from '../models';

export interface IODataResponse<T> {
  value: T[];
}

type MaybeArray<T> = T | T[];

export type DynamicContainerListResponse =
  | IDynamicContainerMetadata
  | IDynamicContainerMetadata[]
  | IODataResponse<IDynamicContainerMetadata>
  | null;

export const toArray = <T>(value: MaybeArray<T> | null | undefined): T[] => {
  if (value == null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

export const toDynamicContainerMetadataList = (
  value: DynamicContainerListResponse
): IDynamicContainerMetadata[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if ('value' in value) {
    return Array.isArray(value.value) ? value.value : [];
  }

  return [value];
};

/**
 * Builds the `/api/DynamicContainer/all(/published)` container-list request with
 * a deploy-skew-safe fallback.
 *
 * The prod nav reads the published-only feed (`/all/published`, WI 30576). If
 * that feed is unavailable, e.g. the deployed Dynamic Content API predates the
 * `/all/published` endpoint during a frontend/backend deploy skew and returns
 * 404, the request falls back to `/all` so the Toolbox degrades to the legacy
 * nav instead of coming back empty and rendering every form blank (Bug 31924,
 * live incident 2026-09-08). A healthy published response is used as-is with no
 * second request.
 *
 * `httpGet` is the raw GET (errors flow through); errors are swallowed to `null`
 * here so an empty published feed and a failed one are distinguished by the
 * caller only through the fallback, never by an unhandled error.
 */
export function buildContainerListRequest(
  httpGet: (url: string) => Observable<IDynamicContainerMetadata[]>,
  onError: (err: unknown) => void,
  publishedUrl: string,
  fallbackUrl: string,
  publishedOnly: boolean
): Observable<IDynamicContainerMetadata[] | null> {
  const get = (url: string): Observable<IDynamicContainerMetadata[] | null> =>
    httpGet(url).pipe(
      catchError((err) => {
        onError(err);
        // Plain `of(null)` (no type argument): under the app build's non-strict
        // tsconfig, `of<IDynamicContainerMetadata[] | null>(null)` collapses the
        // union and resolves rxjs's `of` overload to the array's element type,
        // leaking a singular `IDynamicContainerMetadata` out of catchError and
        // failing the stg build with TS2322. The `null` unions cleanly here.
        return of(null);
      })
    );

  const primary$ = get(publishedUrl);

  return publishedOnly
    ? primary$.pipe(
        switchMap((list) => (list != null ? of(list) : get(fallbackUrl)))
      )
    : primary$;
}
